'use client';
import { useState } from 'react';
import Link from 'next/link';


const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Live Now', href: '/live-now' },
  { label: 'Schedule', href: '/schedule' },
  { label: 'Cricket', href: '/cricket' },
  { label: 'Football', href: '/football' },
  { label: 'Formula 1', href: '/formula1' },
];

const allSports = [
  { label: 'Tennis', href: '/tennis' },
  { label: 'MotoGP', href: '/motogp' },
  { label: 'Golf', href: '/golf' },
  { label: 'Motorsports', href: '/motorsports' },
  { label: 'Basketball', href: '/basketball' },
  { label: 'Badminton', href: '/badminton' },
];

export default function Navbar() {
  const [showAllSports, setShowAllSports] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-rc-nav border-b border-rc-border">
        <nav className="flex items-center h-14 px-4 max-w-[1440px] mx-auto">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0 mr-8">
            <div className="flex items-center">
              <span className="text-2xl font-black tracking-tight">
                <span className="text-rc-orange">RED</span>
                <span className="text-white">CARD</span>
              </span>
            </div>
          </Link>

          {/* Nav Links */}
          <ul className="flex items-center gap-1 flex-1">
            {navLinks?.map((link) => (
              <li key={link?.label}>
                <Link
                  href={link?.href}
                  onClick={() => setActiveLink(link?.label)}
                  className={`nav-link px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    activeLink === link?.label
                      ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-rc-orange' :'text-gray-400 hover:text-white'
                  }`}
                >
                  {link?.label}
                  {link?.label === 'Live Now' && (
                    <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-rc-orange live-pulse"></span>
                  )}
                </Link>
              </li>
            ))}
            {/* All Sports Dropdown */}
            <li className="relative">
              <button
                onClick={() => setShowAllSports(!showAllSports)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                All Sports
                <svg className={`w-3 h-3 transition-transform ${showAllSports ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAllSports && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-rc-card border border-rc-border rounded-lg shadow-xl py-1 z-50">
                  {allSports?.map((sport) => (
                    <Link
                      key={sport?.label}
                      href={sport?.href}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-rc-border transition-colors"
                      onClick={() => setShowAllSports(false)}
                    >
                      {sport?.label}
                    </Link>
                  ))}
                </div>
              )}
            </li>
          </ul>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Link
              href="/subscribe"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-rc-card border border-rc-border rounded-full text-sm font-medium text-white hover:border-rc-orange transition-colors"
            >
              <svg className="w-4 h-4 text-rc-orange" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Offers
            </Link>
            <button
              onClick={() => setShowLoginModal(true)}
              className="w-9 h-9 rounded-full bg-rc-card border border-rc-border flex items-center justify-center hover:border-rc-orange transition-colors"
            >
              <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-rc-card border border-rc-border rounded-2xl p-8 w-full max-w-md mx-4 relative">
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-center mb-6">
              <span className="text-3xl font-black">
                <span className="text-rc-orange">RED</span>
                <span className="text-white">CARD</span>
              </span>
              <p className="text-gray-400 mt-2 text-sm">Sign in to access live sports streaming</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Mobile Number / Email</label>
                <input
                  type="text"
                  placeholder="Enter mobile number or email"
                  className="w-full bg-rc-bg border border-rc-border rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-rc-orange text-sm"
                />
              </div>
              <button className="w-full bg-rc-orange hover:bg-primary-dark text-white font-semibold py-3 rounded-lg transition-colors">
                Continue
              </button>
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-rc-border"></div>
                <span className="px-3 text-xs text-gray-500">OR</span>
                <div className="flex-1 border-t border-rc-border"></div>
              </div>
              <button className="w-full bg-rc-bg border border-rc-border hover:border-rc-orange text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>
            <p className="text-center text-xs text-gray-500 mt-4">
              By continuing, you agree to our{' '}
              <span className="text-rc-orange cursor-pointer">Terms & Conditions</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
